import React, { useEffect, useMemo } from 'react';
import Script from 'next/script';
import { isServerSide, useHubSwitch } from '@bpm-web-app/utils';
import { ExpansionPanel } from '../../shared/ui/expansion-panel/expansion-panel';
import styles from './account-support.module.css';
import ChevronRight from '../../../assets/icons/chevron-right.svg';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AccountSupportProps { }
export function AccountSupport(props: AccountSupportProps) {
    const { isArtistPlatform, isCreate } = useHubSwitch();
    const supportLink = useMemo(() => {
        if (isCreate) return 'https://bpmcreate.zendesk.com/hc/en-us';
        if (isArtistPlatform) return 'https://support.bpmsupreme.com/';
        return 'https://support.bpmsupreme.com/';
    }, [isArtistPlatform, isCreate]);
    const space = useMemo(
        () => (
            <>
                <br />
                <br />
            </>
        ),
        []
    );
    const sections = useMemo(
        () => [
            {
                question: 'What forms of payment do you accept?',
                answer: 'We accept all major credit cards such as Visa, Mastercard, and Discover, as well as payment via PayPal.',
            },
            {
                question: 'On what date will I be charged again with an active subscription?',
                answer: (
                    <>
                        Your membership subscription automatically renews every month from your initial date of sign up. For example: If you signed up on March 15th, your next billing date would be
                        April 15th.
                        {space}
                        Note payments are automatic, should you wish to avoid future charges make sure to cancel at least 72 hours prior to your billing date to ensure no funds will be applied to your
                        account. Automatic payments can begin to process up to 48 hours before your billing date so it is imperative the subscription is canceled within that 72-hour window.
                        {space}
                        No refunds will be issued once payment has been applied to your account.
                        {space}
                        <a href="https://www.bpmsupreme.com/refund-policy">https://www.bpmsupreme.com/refund-policy</a>
                    </>
                ),
            },
            {
                question: <>How do promotions work?</>,
                answer: (
                    <>
                        Each promotion will vary. Please note promotions do not roll over onto future billing. The promotion will only be applied to the first billing cycle. Note any upgrades to the
                        Premium membership are subject to an upgrade fee. If you have questions about a specific promo code please email{' '}
                        <a href="mailto:info@bpmsupreme.com?subject=Contact">info@bpmsupreme.com</a> for more details on your code or review the Terms of Use.
                    </>
                ),
            },
            {
                question: <>How do I update my payment method and/or membership?</>,
                answer: (
                    <>
                        The best way to change your credit card information or change memberships is by canceling your current membership. We recommend canceling today to ensure no payment is applied,
                        you will still have access to your account for the <b>full month</b> you paid for.
                        {space}
                        We advise canceling at least 72 hours prior to your next billing date to ensure no funds are applied to your account. Recurring payments can begin to process 48 hours earlier
                        than the billing date so canceling 72 hours prior to the billing date will ensure the subscription is canceled in a timely manner.
                        {space}
                        Once your account has expired, log into your account and you will be asked to renew your account. When you renew your account, you can then choose the membership you would like
                        and enter your new billing information. You can change your payment method in your account under:
                        {space}
                        <ul>
                            <li>My account</li>
                            <li>Membership info</li>
                            <li>Update my plan</li>
                        </ul>
                        If you have any issue or questions please contact BPM for assistance.
                    </>
                ),
            },
            {
                question: <>How do I cancel my membership?</>,
                answer: (
                    <>
                        Members can cancel anytime. No contract or commitment. Please ensure that you cancel your account <b>72 hours</b> before your next payment date. If you don&apos;t, then your
                        account will be charged for the next month. BPM Supreme does not offer refunds when this occurs, but you will be able to access your account for the full month you paid for.
                        {space}
                        When you cancel your membership before your next payment is due, you still have access until the current month cycle ends from your original payment date.
                        {space}
                        <a href="https://bpmsupreme.com/refund-policy">REFUND POLICY</a>
                    </>
                ),
            },
        ],
        []
    );

    useEffect(() => {
        if (!isServerSide() && (window as any).zE) {
            /* show zendesk widget
             * Taken from https://developer.zendesk.com/api-reference/widget/core/#updatesettings */
            (window as any).zE('webWidget', 'updateSettings', {
                webWidget: {
                    contactForm: {
                        suppress: false,
                    },
                    helpCenter: {
                        suppress: false,
                    },
                    chat: {
                        suppress: false,
                    },
                    talk: {
                        suppress: false,
                    },
                    answerBot: {
                        suppress: false,
                    },
                },
            });
        }

        return () => {
            /* hide zendesk widget */
            (window as any).zE('webWidget', 'updateSettings', {
                webWidget: {
                    contactForm: {
                        suppress: true,
                    },
                    helpCenter: {
                        suppress: true,
                    },
                    chat: {
                        suppress: true,
                    },
                    talk: {
                        suppress: true,
                    },
                    answerBot: {
                        suppress: true,
                    },
                },
            });
        };
    }, []);
    return (
        <div className={styles['account-support']}>
            {/* Start of bpmsupreme Zendesk Widget script */}
            <Script id="ze-snippet" src="https://static.zdassets.com/ekr/snippet.js?key=bef5b9f3-338d-46e0-a9eb-c52898598a98" />
            {/* End of bpmsupreme Zendesk Widget script */}
            <h2 className={styles['account-support__desktop-section-title']}>Support</h2>
            <h2 className={styles['account-support__heading']}>Frequently Asked Questions</h2>
            {sections.map(({ question, answer }, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className={styles['account-support__panel']} key={index}>
                    <ExpansionPanel label={question} content={answer} />
                </div>
            ))}
            <div className={styles['account-support__more-section']}>
                <h3>Still have questions?</h3>
                <a className={styles['account-support__more-link']} href={supportLink} target="_blank" rel="noreferrer">
                    Go to BPM Support
                    <ChevronRight />
                </a>
            </div>
        </div>
    );
}

export default AccountSupport;
